import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';

/**
 * useUnsavedChangesGuard - Warns the user before leaving a form with unsaved edits.
 *
 * Works with the legacy <BrowserRouter> (no data-router required).
 * - Browser close / refresh: native `beforeunload` prompt.
 * - In-app navigation: call `guardedNavigate(path)` instead of `navigate(path)`.
 *   If the form is dirty it opens a confirmation dialog; otherwise it navigates immediately.
 */
export const useUnsavedChangesGuard = () => {
  const isEdited = useAppSelector((state) => state.workflow.isEdited);
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const pendingPath = useRef<string | null>(null);

  // Browser close / refresh guard
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isEdited) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEdited]);

  /** Use this instead of navigate() — it gates on unsaved changes. */
  const guardedNavigate = useCallback(
    (path: string) => {
      if (isEdited) {
        pendingPath.current = path;
        setShowDialog(true);
      } else {
        navigate(path);
      }
    },
    [isEdited, navigate],
  );

  const confirmLeave = useCallback(() => {
    setShowDialog(false);
    if (pendingPath.current) {
      navigate(pendingPath.current);
      pendingPath.current = null;
    }
  }, [navigate]);

  const cancelLeave = useCallback(() => {
    setShowDialog(false);
    pendingPath.current = null;
  }, []);

  return { showDialog, confirmLeave, cancelLeave, guardedNavigate };
};
